import mapboxgl from "mapbox-gl";
import type { RigFracColorSource } from "stores/map";
import { buildColormapPalette } from "utils/color/buildColormapPalette";
import {
  NA_MATCH_KEY,
  RIG_FRAC_NA_COLOR,
  normalizeColorKey,
  normalizeMatchKey
} from "utils/color/focusColorSync";

import type { IColorPalette } from "models/colorPalette";

import { generateColors } from "components/project/shapefiles/mutations/apply-color-palette-to-shapefile-attributes/colorPaletteUtils";

/**
 * On Azure, rig/frac layers start on Plasma, independent of the user's active
 * well or facility palette. On AWS the callers pass the main focus's palette as
 * `defaultPalette` instead (EVA-338). Applying a palette from Colour Actions
 * still overrides either per layer.
 *
 * Built through the same helper that produces the palette list's "Plasma" entry,
 * so the default and an explicit pick of Plasma cannot drift apart. Its explicit
 * colours also matter downstream: the colormap package refuses plasma below nine
 * shades, so generateColors spreads them for smaller domains.
 */
export const RIG_FRAC_DEFAULT_PALETTE: Nullable<IColorPalette> =
  buildColormapPalette("plasma");

export interface RigFracColorAssignmentParams {
  // The attribute key to color by. Every production caller resolves this
  // through resolveColorByField, which is total, so the absent case below is a
  // defensive guard only — a missing key would otherwise build a `["get",
  // undefined]` expression that throws inside Mapbox.
  colorByField: Maybe<string>;
  // Sorted value domain for the field, from the values endpoint.
  domain: Maybe<string[]>;
  // Remembered colouring source (last applied palette / single colour). When
  // present it drives the base colour for the whole domain, so a Color By switch
  // keeps the user's last choice. Overrides still win per value. Absent falls
  // back to RIG_FRAC_DEFAULT_PALETTE.
  colorSource?: Maybe<RigFracColorSource>;
  // Per-value hex overrides; take precedence over palette-assigned colors.
  overrides: Record<string, string>;
  // The layer's static default color — the match's fallback branch and the
  // return value when no coloring applies.
  staticColor: string;
  // Well focus colours to sync onto this domain, or undefined to color as if
  // no focus were active. The caller decides whether this applies (via
  // shouldApplyFocusColors in utils/color/focusColorSync/gate.ts) — this
  // module only ever consumes the result, never re-derives the gate.
  focus?: Maybe<RigFracFocusColorInputs>;
  // The palette to spread when nothing else applies (no remembered choice, no
  // focus base). Defaults to Plasma. The rig/frac charts pass a reversed Plasma
  // when the user reverses it in the chart's own palette picker, which nothing
  // else on this path would notice: a reversed default keeps the same id, so it
  // still counts as the default everywhere the id is what is checked.
  defaultPalette?: Maybe<IColorPalette>;
  // Values that must also receive a colour even though they are not in the
  // domain. A chart plots whatever series the server returns, and those labels
  // do not always all appear in the value domain; without this they would fall
  // back to the server's own colour, which is a different palette entirely and
  // reads as the feature not working.
  //
  // They cannot be appended to `domain` instead: the palette is generated to
  // the domain's length, so a longer domain changes every colour and the chart
  // would stop agreeing with the map. These are coloured after the domain, from
  // what the palette has left over, and never move a domain value's colour.
  extraValues?: Maybe<string[]>;
  // Whether a blank value takes the shared n/a grey (RIG_FRAC_NA_COLOR)
  // regardless of colorSource. Defaults to `colorSource == null`, which is
  // today's behaviour for the map adapter and the layer panel — both pass a
  // real per-layer colorSource once the user has picked one, and deliberately
  // keep painting a blank value from it like any other value. The chart hook
  // passes `true` explicitly: it needs the shared grey to survive even while
  // colouring from the user's own chart palette (colorSource is non-null
  // there on that path).
  blankTakesNaColor?: boolean;
}

export interface RigFracFocusColorInputs {
  // normalized match key -> hex, from the active focus resolution.
  colors: ReadonlyMap<string, string>;
  // normalized hexes the focus bins consume, matched or not — kept out of
  // the generated palette's front so unmatched values don't collide with a
  // colour a focus bin already owns.
  reservedColors: ReadonlySet<string>;
  // The user's active well palette, spread over values the focus doesn't
  // match. Falls back to RIG_FRAC_DEFAULT_PALETTE when absent.
  basePalette?: Maybe<IColorPalette>;
}

export interface RigFracColorAssignment {
  // value → hex color (palette assignment with overrides applied). Empty when
  // the layer is on its static color.
  valueColors: Map<string, string>;
  // The Mapbox paint expression, or null when the caller should set the static
  // literal color instead. Never an empty match (Mapbox throws on zero pairs).
  matchExpression: mapboxgl.Expression | null;
}

/**
 * Single source of truth for rig/frac value → color assignment, shared by the
 * map adapter (icon-color) and the panel (swatches) so they can never disagree.
 * An empty or missing domain yields no expression and a signal to use the
 * static color.
 */
export function buildRigFracColorAssignment(
  params: RigFracColorAssignmentParams
): RigFracColorAssignment {
  const {
    colorByField,
    domain,
    colorSource,
    overrides,
    staticColor,
    focus,
    defaultPalette,
    extraValues,
    blankTakesNaColor
  } = params;

  const appliesNaColorToBlank = blankTakesNaColor ?? colorSource == null;

  if (!colorByField || !domain || domain.length === 0) {
    return { valueColors: new Map(), matchExpression: null };
  }

  // A remembered single colour paints the whole domain; a remembered palette (or
  // the focus's base palette, or the default) is spread across it. Persisted
  // overrides win over either.
  const solidColor = colorSource?.kind === "solid" ? colorSource.color : null;
  const sourcePalette =
    colorSource?.kind === "palette"
      ? colorSource.palette
      : focus?.basePalette ?? defaultPalette ?? RIG_FRAC_DEFAULT_PALETTE;

  // An unusable palette (e.g. empty colors + a non-colormap name) can throw
  // inside generateColors. Never let coloring crash the map or panel — fall
  // back to the static color for every value (FR-012).
  let paletteColors: string[] = [];
  if (solidColor === null) {
    try {
      paletteColors = generateColors(sourcePalette, domain.length);
    } catch {
      paletteColors = [];
    }
  }

  // Reserved colours (every focus bin, matched or not) go LAST in the pool
  // instead of being excluded outright — excluding them could empty the pool
  // entirely when a small domain's whole generated palette happens to collide
  // with the reserved set. Order only matters when there's a reserved set to
  // order around; otherwise this is the original sequence.
  const reservedColors = focus?.reservedColors;
  const hasReservedColors = !!reservedColors && reservedColors.size > 0;
  const orderedPaletteColors = hasReservedColors
    ? [
        ...paletteColors.filter((color) => !reservedColors.has(normalizeColorKey(color))),
        ...paletteColors.filter((color) => reservedColors.has(normalizeColorKey(color)))
      ]
    : paletteColors;

  const valueColors = new Map<string, string>();

  // How the pool is consumed depends on whether there is a reserved set:
  //
  // With no focus, each value keeps ITS OWN domain index, so every value's
  // colour is identical to what this function produced before focus inputs and
  // blank handling existed (the byte-identical bar). The pool is sized to the
  // domain, so a gap left by a blank slot can never collide two values.
  //
  // With a reserved set, indexing by domain position would defeat the ordering
  // above entirely: the pool holds exactly one colour per domain value, so
  // every colour in it gets handed out, reserved ones included, and an
  // unmatched value lands on a colour a focus bin already owns. Values that
  // consume a generated colour therefore walk the pool in order instead, which
  // is what actually keeps them off the reserved tail until the free colours
  // run out.
  let paletteCursor = 0;
  domain.forEach((value, index) => {
    // A blank value always gets the shared n/a colour, but only while the
    // layer has no remembered colouring of its own — a solid colour or
    // palette the user already picked still applies to a blank value like
    // any other.
    const isBlank = appliesNaColorToBlank && normalizeMatchKey(value) === NA_MATCH_KEY;
    const focusColor = focus ? focus.colors.get(normalizeMatchKey(value)) : undefined;

    let assigned: string;
    if (isBlank) {
      assigned = RIG_FRAC_NA_COLOR;
    } else if (focusColor != null) {
      assigned = focusColor;
    } else if (solidColor !== null) {
      assigned = solidColor;
    } else {
      const paletteIndex = hasReservedColors ? paletteCursor++ : index;
      assigned =
        orderedPaletteColors[paletteIndex % orderedPaletteColors.length] ?? staticColor;
    }

    // hasOwnProperty rather than a plain lookup so a value named like an
    // Object.prototype member can't inherit one. A stored entry that isn't a
    // usable colour (null, or blank from an older write) falls through to the
    // assigned colour: letting it reach the match expression would make Mapbox
    // reject the whole layer's paint, not just that one value.
    const hasOverride =
      overrides != null && Object.prototype.hasOwnProperty.call(overrides, value);
    const override = hasOverride ? overrides[value] : undefined;
    // A matching focus outranks a per-value override too: while it is active the
    // layer's own colours, however they were set, do not apply.
    valueColors.set(value, focusColor ?? (override || assigned));
  });

  // Values the caller has to paint that the domain does not cover. Each takes a
  // palette colour nothing else is using, so it reads as part of the same
  // palette rather than as an outsider, and only repeats a colour once the
  // palette genuinely has none left. Focus-owned colours stay off limits: they
  // identify a legend bin, and this value by definition did not match one.
  if (extraValues?.length) {
    const assignedColors = new Set(
      [...valueColors.values()].map((color) => normalizeColorKey(color))
    );
    const assignedKeys = new Set([...valueColors.keys()].map(normalizeMatchKey));

    for (const value of extraValues) {
      // Compared on the match key, not the raw string: a label that differs
      // only in case or spacing from a domain value is the same value, and
      // giving it a second colour here would let it overwrite the first one
      // downstream, where both sides re-key by exactly that.
      const key = normalizeMatchKey(value);
      if (assignedKeys.has(key)) {
        continue;
      }

      let assigned: string;
      if (appliesNaColorToBlank && key === NA_MATCH_KEY) {
        assigned = RIG_FRAC_NA_COLOR;
      } else if (focus?.colors.get(key) != null) {
        assigned = focus.colors.get(key) as string;
      } else if (solidColor !== null) {
        assigned = solidColor;
      } else {
        // Preference order, and the reason for each step:
        //
        // 1. A colour nothing is using and no bin reserved. Reserved colours
        //    have to be excluded explicitly rather than by their position: the
        //    pool still contains them (the block above moved them to the tail
        //    instead of dropping them), and a bin whose value has no row in this
        //    domain reserves a colour that never gets assigned, so it stays
        //    eligible the moment the free head runs out. That is the ordinary
        //    case, not a corner: matched and blank values consume no palette
        //    slot, so the free head is short by however many bins matched.
        // 2. Any colour no bin reserved, even one already in use here. Repeating
        //    a colour inside one surface is a smaller lie than wearing the
        //    colour of a legend bin this value did not match, which is the
        //    cross-vocabulary collision the whole reserved set exists to stop.
        // 3. Only if every colour in the palette is reserved, the pool cycle.
        const isReserved = (color: string) =>
          reservedColors?.has(normalizeColorKey(color)) === true;
        const freeAndUnreserved = orderedPaletteColors.find(
          (color) => !assignedColors.has(normalizeColorKey(color)) && !isReserved(color)
        );
        const unreserved = orderedPaletteColors.filter((color) => !isReserved(color));
        assigned =
          freeAndUnreserved ??
          unreserved[paletteCursor++ % unreserved.length] ??
          orderedPaletteColors[paletteCursor++ % orderedPaletteColors.length] ??
          staticColor;
      }

      const hasOverride =
        overrides != null && Object.prototype.hasOwnProperty.call(overrides, value);
      const override = hasOverride ? overrides[value] : undefined;
      const finalColor = focus?.colors.get(key) ?? (override || assigned);
      valueColors.set(value, finalColor);
      assignedKeys.add(key);
      assignedColors.add(normalizeColorKey(finalColor));
    }
  }

  const pairs: string[] = [];
  valueColors.forEach((color, value) => {
    pairs.push(value, color);
  });

  const matchExpression = [
    "match",
    ["get", colorByField],
    ...pairs,
    staticColor
  ] as mapboxgl.Expression;

  return { valueColors, matchExpression };
}
